import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/ui/Button";
import { Checkbox, Field, Input } from "@/ui/Field";
import { AuthCard, FormError, FormNotice } from "@/features/auth/AuthCard";
import { cx } from "@/lib/format";

export const Route = createFileRoute("/_public/register")({
  component: RegisterScreen,
});

const MIN_PASSWORD_LENGTH = 12;

function RegisterScreen() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  // Who to invoice, asked here rather than in a month.
  //
  // Billing is after the fact: an account can run for a month before anything
  // is owed. Collecting an address at that point means asking somebody for
  // their details after the service has already been delivered, which is both
  // worse to do and less likely to work.
  const [fullName, setFullName] = useState("");
  const [addressLine, setAddressLine] = useState("");
  const [postcode, setPostcode] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("DE");
  const [isBusiness, setIsBusiness] = useState(false);
  const [vatID, setVatID] = useState("");
  // Consent is a separate act from filling in the form, so it is a separate
  // control that starts unticked. A pre-ticked box is not consent, and in the
  // one jurisdiction that matters here it is explicitly not consent.
  //
  // It is also sent, which it was not. The box disabled the submit button and
  // went no further: the endpoint had no field for it, so no account on this
  // installation could be shown to have agreed to anything. A disabled button
  // is a convenience for the person filling the form in, not evidence.
  const [agreed, setAgreed] = useState(false);
  // And this is a second, different act, which is why it is a second box
  // rather than another clause inside the first one.
  //
  // A consumer may withdraw within fourteen days. § 357 Abs. 8 BGB lets us
  // charge for what already ran in that time only if they expressly asked for
  // performance to begin — "expressly" meaning a declaration of its own, not a
  // sentence folded into a general acceptance of the terms. Bundled into the
  // box above it would buy nothing: the hours would simply be unbillable.
  const [startNow, setStartNow] = useState(false);

  /**
   * Which half of the form is showing.
   *
   * # WHY THIS IS TWO STEPS AND NOT ONE LONG PAGE
   *
   * It was eleven controls in a 380-pixel column, 1400 pixels tall: your
   * name, then a postal address, then a country code, then a business
   * question, then — after all of that — the email address and password that
   * actually make the account. Somebody arriving to try a beta met a form
   * asking where to post things to, with the parts they expected buried
   * below the fold.
   *
   * The order was the problem as much as the length. So: the account first,
   * because that is what the visitor came to make and it is three fields;
   * then invoicing, introduced, with the reason it is asked at all.
   *
   * # AND WHY THE ADDRESS IS STILL ASKED NOW
   *
   * Not layout cowardice — it is the contract. Billing here is after the
   * fact: an account can run for a month before anything is owed, and
   * collecting an address at that point means asking for details after the
   * service has been delivered. § 14 UStG also wants them on the invoice,
   * and `withdrawal_ack` has to be a declaration made before performance
   * begins to be worth anything. Both are required by the endpoint. What
   * changed is when a person is asked to read them, not whether.
   *
   * One request still goes out, with everything in it, at the end.
   */
  const [step, setStep] = useState<1 | 2>(1);

  const register = useMutation({
    mutationFn: () =>
      api.post<{ message: string }>("/auth/register", {
        email,
        password,
        display_name: displayName,
        full_name: fullName,
        address_line: addressLine,
        postcode,
        city,
        country: country.trim().toUpperCase(),
        is_business: isBusiness,
        vat_id: isBusiness ? vatID.trim().toUpperCase() : "",
        accepted_terms: agreed,
        withdrawal_ack: startNow,
      }),
  });

  const fieldErrors = register.error instanceof ApiError ? register.error.fieldErrors() : {};

  if (register.isSuccess) {
    return (
      <AuthCard
        title="Check your email"
        subtitle={`We sent a confirmation link to ${email}.`}
        footer={
          <Link to="/sign-in" className="font-medium text-[var(--primary)] hover:underline">
            Back to sign in
          </Link>
        }
      >
        <FormNotice title="Confirm your address to finish setting up your account.">
          The link expires in 48 hours. If it does not arrive, check your spam folder or{" "}
          <Link to="/verify-email" search={{ email }} className="underline">
            request another one
          </Link>
          .
        </FormNotice>
        <p className="mt-4 text-[13px] leading-relaxed text-[var(--text-secondary)]">
          After that your account joins the beta queue. Drumlin is not open to everybody yet.
          A person reviews each request, and you will be emailed when yours is approved.
        </p>
      </AuthCard>
    );
  }

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const accountReady =
    displayName.trim().length > 0 &&
    email.trim().length > 0 &&
    password.length >= MIN_PASSWORD_LENGTH;

  // A field error from the server may belong to the step that is not
  // showing. Sending somebody to "Request access" and having nothing visibly
  // change is the worst way to report a problem, so the form goes back to
  // where the problem is.
  const firstStepFields = ["display_name", "email", "password"];
  const errorIsOnStepOne = Object.keys(fieldErrors).some((f) => firstStepFields.includes(f));
  const showing = errorIsOnStepOne ? 1 : step;

  return (
    <AuthCard
      title={showing === 1 ? "Request beta access" : "Who to invoice"}
      subtitle={
        showing === 1
          ? "Drumlin is in beta. Accounts are approved by a person, usually within a day."
          : "Drumlin bills monthly for what you used, so an invoice needs a name and an address. You can change these later."
      }
      above={<Steps current={showing} />}
      footer={
        showing === 1 ? (
          <>
            Already have an account?{" "}
            <Link to="/sign-in" className="font-medium text-[var(--primary)] hover:underline">
              Sign in
            </Link>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setStep(1)}
            className="font-medium text-[var(--primary)] hover:underline"
          >
            &larr; Back to your account
          </button>
        )
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          // The first step's controls are not in the tree on the second, so
          // the browser cannot validate them and a mistake there would
          // otherwise only surface as a field error from the server.
          if (showing === 1) {
            if (accountReady) setStep(2);
            return;
          }
          register.mutate();
        }}
      >
        {showing === 1 ? (
          <>
            <Field label="Your name" error={fieldErrors.display_name} hint="How you appear to colleagues.">
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  name="name"
                  autoComplete="name"
                  autoFocus
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  aria-describedby={describedBy}
                  invalid={invalid}
                />
              )}
            </Field>

            <Field label="Email address" required error={fieldErrors.email}>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="email"
                  name="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-describedby={describedBy}
                  invalid={invalid}
                />
              )}
            </Field>

            <Field
              label="Password"
              required
              error={fieldErrors.password}
              hint={`At least ${MIN_PASSWORD_LENGTH} characters. A memorable phrase beats a short, complicated word.`}
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  type="password"
                  name="password"
                  autoComplete="new-password"
                  required
                  minLength={MIN_PASSWORD_LENGTH}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-describedby={describedBy}
                  invalid={invalid || tooShort}
                />
              )}
            </Field>

            {register.error && !Object.keys(fieldErrors).length && (
              <FormError error={register.error} />
            )}

            <Button type="submit" tone="primary" wide disabled={!accountReady}>
              Continue
            </Button>
          </>
        ) : (
          <>
            <Field label="Full name or company" required error={fieldErrors.full_name}>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  name="organization"
                  autoComplete="organization"
                  autoFocus
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  aria-describedby={describedBy}
                  invalid={invalid}
                />
              )}
            </Field>

            <Field label="Street and number" required error={fieldErrors.address_line}>
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  name="street-address"
                  autoComplete="street-address"
                  value={addressLine}
                  onChange={(e) => setAddressLine(e.target.value)}
                  aria-describedby={describedBy}
                  invalid={invalid}
                />
              )}
            </Field>

            <div className="grid gap-4 sm:grid-cols-[7rem_1fr_6rem]">
              <Field label="Postcode" required error={fieldErrors.postcode}>
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    name="postal-code"
                    autoComplete="postal-code"
                    value={postcode}
                    onChange={(e) => setPostcode(e.target.value)}
                    aria-describedby={describedBy}
                    invalid={invalid}
                  />
                )}
              </Field>
              <Field label="City" required error={fieldErrors.city}>
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    name="address-level2"
                    autoComplete="address-level2"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    aria-describedby={describedBy}
                    invalid={invalid}
                  />
                )}
              </Field>
              {/* On the same line as the rest of the address, which is where
                  it is on an envelope. It had a line of its own and a hint
                  two sentences long for a two-letter box. */}
              <Field label="Country" required error={fieldErrors.country}>
                {({ id, describedBy, invalid }) => (
                  <Input
                    id={id}
                    name="country"
                    autoComplete="country"
                    maxLength={2}
                    className="uppercase"
                    value={country}
                    onChange={(e) => setCountry(e.target.value.replace(/[^a-zA-Z]/g, ""))}
                    aria-describedby={describedBy}
                    invalid={invalid}
                  />
                )}
              </Field>
            </div>
            <p className="t-meta -mt-1">
              The country decides the VAT on your invoices, so it is asked
              rather than guessed.
            </p>

            {/* Business or not, and the identifier if there is one.
                Asked here rather than on the billing screen because it
                decides the tax on the first invoice, and the first invoice
                goes out before most people have opened a settings page. A
                business in another EU country with a confirmed number pays
                no German VAT; the same business without one pays it and has
                to ask for it back. */}
            <div className="border-t border-[var(--border)] pt-4">
              <Checkbox
                checked={isBusiness}
                onChange={(e) => {
                  setIsBusiness(e.target.checked);
                  if (!e.target.checked) setVatID("");
                }}
                label="This account is for a business"
              />
            </div>

            {isBusiness && (
              <div className="animate-hood">
                <Field
                  label="VAT identification number"
                  error={fieldErrors.vat_id}
                  hint="Optional. With one confirmed by the European register, a business outside Germany is invoiced without VAT."
                >
                  {({ id, describedBy, invalid }) => (
                    <Input
                      id={id}
                      name="vat_id"
                      autoComplete="off"
                      maxLength={20}
                      className="uppercase"
                      placeholder="DE123456789"
                      value={vatID}
                      onChange={(e) => setVatID(e.target.value.replace(/[^a-zA-Z0-9]/g, ""))}
                      aria-describedby={describedBy}
                      invalid={invalid}
                    />
                  )}
                </Field>
              </div>
            )}

            <div className="flex flex-col gap-3 border-t border-[var(--border)] pt-4">
              <Checkbox
                required
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                label={
                  <span>
                    I agree to the{" "}
                    <a
                      href="https://drumlin.cloud/legal/agb"
                      target="_blank"
                      rel="noreferrer"
                      className="text-[var(--primary)] underline underline-offset-2"
                    >
                      terms of service
                    </a>{" "}
                    and have read the{" "}
                    <a
                      href="https://drumlin.cloud/legal/datenschutz"
                      target="_blank"
                      rel="noreferrer"
                      className="text-[var(--primary)] underline underline-offset-2"
                    >
                      privacy notice
                    </a>
                    .
                  </span>
                }
              />

              <Checkbox
                required
                checked={startNow}
                onChange={(e) => setStartNow(e.target.checked)}
                label={
                  <span>
                    I ask Drumlin to start running my services straight away,
                    before the 14-day withdrawal period ends, and understand
                    that if I then withdraw I owe the hours that already ran.
                    My{" "}
                    <a
                      href="https://drumlin.cloud/legal/widerruf"
                      target="_blank"
                      rel="noreferrer"
                      className="text-[var(--primary)] underline underline-offset-2"
                    >
                      right of withdrawal
                    </a>{" "}
                    is otherwise unaffected.
                  </span>
                }
              />
            </div>

            {register.error && !Object.keys(fieldErrors).length && (
              <FormError error={register.error} />
            )}

            <Button
              type="submit"
              tone="primary"
              wide
              busy={register.isPending}
              disabled={!agreed || !startNow}
            >
              Request access
            </Button>
          </>
        )}
      </form>
    </AuthCard>
  );
}

/**
 * Where you are in the form.
 *
 * Two words and a rule rather than "Step 1 of 2" over a progress bar: there
 * are two steps, both are named, and the one you are on is the dark one. A
 * bar would be measuring something nobody is anxious about.
 */
function Steps({ current }: { current: 1 | 2 }) {
  return (
    <ol className="mb-5 flex items-center gap-2">
      {[
        { n: 1, label: "Your account" },
        { n: 2, label: "Invoicing" },
      ].map((step) => (
        <li key={step.n} className="flex flex-1 flex-col gap-1.5">
          <span
            className={cx(
              "h-[3px] rounded-full transition-colors duration-[var(--d-normal)]",
              step.n <= current ? "bg-[var(--primary)]" : "bg-[var(--border)]",
            )}
          />
          <span
            className={cx(
              "section-label transition-colors duration-[var(--d-normal)]",
              step.n === current && "text-[var(--text-primary)]",
            )}
          >
            {step.label}
          </span>
        </li>
      ))}
    </ol>
  );
}
